import React, { useState } from 'react';
import { format, addWeeks, subWeeks, startOfWeek, addDays, isSameDay, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { nb } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import BookingDetailsDialog from './BookingDetailsDialog';
import { Booking } from '@/types/booking';
import { toast } from 'sonner';

interface WeeklyCalendarProps {
  bookings: Booking[];
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({ bookings }) => {
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const handleDeleteBooking = (bookingId: string) => {
    toast.success(`Booking ${bookingId} deleted successfully`);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeek(direction === 'prev' ? subWeeks(currentWeek, 1) : addWeeks(currentWeek, 1));
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(currentWeek, i));
    }
    return days;
  };

  const getBookingsForDay = (day: Date) => {
    return bookings.filter(booking => 
      isWithinInterval(day, {
        start: startOfDay(booking.startDate),
        end: endOfDay(booking.endDate)
      })
    );
  };

  const getBookingForHour = (day: Date, hour: number) => {
    const dayBookings = getBookingsForDay(day);
    
    for (const booking of dayBookings) {
      const bookingStart = booking.startDate;
      const bookingEnd = booking.endDate;
      
      // Check if this hour falls within the booking period
      const currentHour = new Date(day);
      currentHour.setHours(hour, 0, 0, 0);
      
      const nextHour = new Date(currentHour);
      nextHour.setHours(hour + 1, 0, 0, 0);
      
      if (isWithinInterval(currentHour, { start: bookingStart, end: bookingEnd }) ||
          isWithinInterval(nextHour, { start: bookingStart, end: bookingEnd }) ||
          (currentHour <= bookingStart && nextHour >= bookingEnd)) {
        return booking;
      }
    }
    
    return null;
  };

  const weekDays = getWeekDays();
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Ukeoversikt</CardTitle>
            <CardDescription>
              {format(currentWeek, 'dd. MMMM', { locale: nb })} - {format(addDays(currentWeek, 6), 'dd. MMMM yyyy', { locale: nb })}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigateWeek('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
              I dag
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigateWeek('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto">
          <div className="min-w-[800px]">
            {/* Header with day names */}
            <div className="grid grid-cols-8 gap-1 mb-2">
              <div className="text-xs font-medium text-muted-foreground p-2">Timer</div>
              {weekDays.map((day) => (
                <div key={day.toISOString()} className="text-center p-2 border rounded-lg bg-muted/30">
                  <div className="text-sm font-medium">
                    {format(day, 'EEE', { locale: nb })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(day, 'dd.MM', { locale: nb })}
                  </div>
                </div>
              ))}
            </div>

            {/* Hour slots */}
            <div className="space-y-1">
              {hours.map((hour) => (
                <div key={hour} className="grid grid-cols-8 gap-1">
                  <div className="text-xs text-muted-foreground p-1 flex items-center justify-end pr-2">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {weekDays.map((day) => {
                    const booking = getBookingForHour(day, hour);
                    const isBooked = !!booking;
                    
                    return (
                      <div
                        key={`${day.toISOString()}-${hour}`}
                        className={`h-8 border rounded transition-colors ${
                          isBooked 
                            ? 'bg-green-500 hover:bg-green-600 cursor-pointer border-green-600' 
                            : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                        }`}
                        title={isBooked ? `${booking.companyName} - ${booking.articleTitle || 'Ingen tittel'}` : ''}
                      >
                        {isBooked && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <div 
                                className="w-full h-full flex items-center justify-center cursor-pointer"
                                onClick={() => setSelectedBooking(booking)}
                              >
                                <div className="w-1 h-1 bg-white rounded-full opacity-60" />
                              </div>
                            </DialogTrigger>
                            <BookingDetailsDialog booking={booking} onDelete={handleDeleteBooking} />
                          </Dialog>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded border" />
            <span>Booket tid</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-50 rounded border border-gray-200" />
            <span>Ledig tid</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyCalendar;